import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { ApproveTerminationDialogComponent } from 'src/app/dialogs/approve-termination-dialog/approve-termination-dialog.component';
import { CreateCreditPlanDialogComponent } from 'src/app/dialogs/create-credit-plan-dialog/create-credit-plan-dialog.component';
import { CreateSupplierDialogComponent } from 'src/app/dialogs/create-supplier-dialog/create-supplier-dialog.component';
import { DeleteSupplierDialogComponent } from 'src/app/dialogs/delete-supplier-dialog/delete-supplier-dialog.component';
import { EditBeyondSupplierDialogComponent } from 'src/app/dialogs/edit-beyond-supplier-dialog/edit-beyond-supplier-dialog.component';
import { EditCreditPlanDialogComponent } from 'src/app/dialogs/edit-credit-plan-dialog/edit-credit-plan-dialog.component';
import { EditSupplierDialogComponent } from 'src/app/dialogs/edit-supplier-dialog/edit-supplier-dialog.component';
import { GetTransactionsDialogComponent } from 'src/app/dialogs/get-transactions-dialog/get-transactions-dialog.component';
import { PermissionGroupDialogComponent } from 'src/app/dialogs/permission-group-dialog/permission-group-dialog.component';
import { SuspendBeyondSupplierDialogComponent } from 'src/app/dialogs/suspend-beyond-supplier-dialog/suspend-beyond-supplier-dialog.component';
import { TerminateBeyondSupplierDialogComponent } from 'src/app/dialogs/terminate-beyond-supplier-dialog/terminate-beyond-supplier-dialog.component';
import { BeyondSupplier } from 'src/app/model/beyond-supplier';
import { CreditPlan } from 'src/app/model/credit-plan';
import { TerminationRequest } from 'src/app/model/termination-request';
import { Supplier } from 'src/app/model/supplier';
import { ViewTerminationDialogComponent } from 'src/app/dialogs/view-termination-dialog/view-termination-dialog.component';

/**
 * Dialog service handles opening all dialogs in the application
 */
@Injectable({
  providedIn: 'root'
})
export class DialogService {
  /** Whether a dialog is open or not */
  private _dialogOpen = false;

  /**
   * Default constructor for the dialog service
   * 
   * @param dialog
   */
  constructor(
    public dialog: MatDialog,
  ) { }

  /**
   * Open up the edit supplier dialog and return the edited supplier
   */
  public openEditSupplier(supplier: Supplier): Observable<any> {
    // Open the dialog
    const dialogRef = this.dialog.open(EditSupplierDialogComponent, {
      width: "50vw",
      height: "100vh",
      position: {
        right: "0",
      },
      data: {
        supplier: supplier,
      }
    });

    // Return an observable containing the updated supplier
    return dialogRef.afterClosed();
  }

  /**
   * Open up the create supplier dialog and return the edited supplier
   */
  public openCreateSupplier(): Observable<any> {
    // Open the dialog
    const dialogRef = this.dialog.open(CreateSupplierDialogComponent, {
      width: "50vw",
      height: "100vh",
      position: {
        right: "0",
      },
    });

    // Return an observable containing the new supplier
    return dialogRef.afterClosed();
  }

  /**
   * Open up the delete supplier confirmation page
   */
  public openDeleteSupplier(supplier: Supplier): Observable<any> {
    const dialogRef = this.dialog.open(DeleteSupplierDialogComponent, {
      width: "50vw",
      height: "70vh",
      data: {
        supplier: supplier,
      },
    });

    // Return an observable containing the confirmation status
    return dialogRef.afterClosed();
  }

  /**
   * Open up the get transactions dialog
   */
  public openGetTransactions(): Observable<any> {
    // Open the dialog
    const dialogRef = this.dialog.open(GetTransactionsDialogComponent, {
      width: "30vw",
      height: "50vh",
    });

    // Return an observable containing the card number
    return dialogRef.afterClosed();
  }

  /**
   * Open up the edit credit plan dialog and return the edited credit plan
   */
  public openEditCreditPlan(plan: CreditPlan): Observable<any> {
    // Open the dialog
    const dialogRef = this.dialog.open(EditCreditPlanDialogComponent, {
      width: "50vw",
      height: "100vh",
      position: {
        right: "0",
      },
      data: {
        creditPlan: plan,
      }
    });

    // Return an observable containing the updated supplier
    return dialogRef.afterClosed();
  }

  /**
   * Open up the create credit plan dialog and return the edited supplier
   */
  public openCreateCreditPlan(): Observable<any> {
    // Open the dialog
    const dialogRef = this.dialog.open(CreateCreditPlanDialogComponent, {
      width: "50vw",
      height: "100vh",
      position: {
        right: "0",
      },
    });

    // Return an observable containing the new supplier
    return dialogRef.afterClosed();
  }

  /**
   * Open the permission group dialog and return the selected permission group
   */
  public openPermissionGroupDialog(name: string): Observable<string | null> {
    // Open the dialog
    const dialogRef = this.dialog.open(PermissionGroupDialogComponent, {
      width: "50vw",
      height: "60vh",
      data: {
        name: name,
      }
    });

    // Return an observable containing the new permission group
    return dialogRef.afterClosed();
  }

  /**
   * Open the edit beyond supplier dialog and return the edited beyond supplier
   */
  public openEditBeyondSupplier(beyondSupplier: BeyondSupplier): Observable<BeyondSupplier> {
    // Open the dialog
    const dialogRef = this.dialog.open(EditBeyondSupplierDialogComponent, {
      width: "40vw",
      height: "100vh",
      position: {
        right: "0",
      },
      data: {
        beyondSupplier: beyondSupplier,
      }
    });

    // Return an observable containing the updated beyond supplier
    return dialogRef.afterClosed();
  }

  /**
   * Open the suspend beyond supplier dialog and return confirmation
   */
  public openSuspendBeyondSupplier(beyondSupplier: BeyondSupplier): Observable<boolean> {
    // Open the dialog
    const dialogRef = this.dialog.open(SuspendBeyondSupplierDialogComponent, {
      width: "50vw",
      data: {
        beyondSupplier: beyondSupplier,
      }
    });

    // Return an observable containing the confirmation status
    return dialogRef.afterClosed();
  }

  /**
   * Open the terminate beyond supplier dialog and return confirmation
   */
  public openTerminateBeyondSupplier(beyondSupplier: BeyondSupplier): Observable<boolean> {
    // Open the dialog
    const dialogRef = this.dialog.open(TerminateBeyondSupplierDialogComponent, {
      width: "50vw",
      data: {
        beyondSupplier: beyondSupplier,
      }
    });

    // Return an observable containing the confirmation status
    return dialogRef.afterClosed();
  }

  /**
   * Open the approve termination dialog and return the action result
   * 
   * @param terminationRecord The termination record to provide to the dialog
   */
  public openApproveTerminationDialog(terminationRecord: TerminationRequest): Observable<string | null> {
    // Open the dialog
    const dialogRef = this.dialog.open(ApproveTerminationDialogComponent, {
      width: "60vw",
      maxHeight: "80vh",
      data: {
        terminationRecord: terminationRecord,
      }
    });

    // Return an observable containing the action result
    return dialogRef.afterClosed();
  }

  /**
   * Open the view termination dialog and return the action result
   * 
   * @param terminationRecord The termination record to provide to the dialog
   */
  public openViewTerminationDialog(terminationRecord: TerminationRequest): Observable<string | null> {
    // Open the dialog
    const dialogRef = this.dialog.open(ViewTerminationDialogComponent, {
      width: "60vw",
      maxHeight: "80vh",
      data: {
        terminationRecord: terminationRecord,
      }
    });

    // Return an observable containing the action result
    return dialogRef.afterClosed();
  }
}
